import { Segment, Menu, Header, Grid, Dropdown, Confirm } from "semantic-ui-react";
import LicencesGrid from "./LicencesGrid";
import { Licence } from "../models/Licence";
import { AsyncActionStatus } from "../models/AsyncActionStatus";
import { useEffect, useState } from "react";
import { toastSuccess } from "../utils";
import AddLicenceModal from "./AddLicenceModal";
import { LicenceProduct } from "../models/LicenceProduct";
import ChangeLicenceProductModal from "./ChangeLicenceProductModal";

interface Props {
  isLicencesLoading: boolean;
  licences?: Licence[] | null;
  pageCount?: number;
  onPageInfoChange: (pageSize: number, pageIndex: number) => void;
  onOrgFilter: (orgId: string) => void;
  onAddLicence: (productName: string, totalPrice: number, numberOfUsers: number, licenceType: string) => void;
  addLicenceStatus: AsyncActionStatus;
  onRenewLicence: (
    productName: string,
    totalPrice: number,
    numberOfUsers: number,
    licenceType: string,
    licenceId: string,
  ) => void;
  renewLicenceStatus: AsyncActionStatus;
  onCancelAutoRenewal: (licenceId: string) => void;
  cancelAutoRenewalStatus: AsyncActionStatus;
  onArchiveLicence: (id: string) => void;
  archiveLicenceStatus: AsyncActionStatus;
  isArchivedFilterValue?: boolean | null;
  onIsArchivedFilter: (isArchived: boolean) => void;
  products?: LicenceProduct[] | null;
}

const LicencesList = ({
  isLicencesLoading,
  licences,
  pageCount,
  onPageInfoChange,
  onAddLicence,
  addLicenceStatus,
  onRenewLicence,
  renewLicenceStatus,
  onCancelAutoRenewal,
  cancelAutoRenewalStatus,
  archiveLicenceStatus,
  isArchivedFilterValue,
  onIsArchivedFilter,
  products,
}: Props) => {
  const [openAddLicenceModal, setOpenAddLicenceModal] = useState(false);
  const [selectedLicenceToRenew, setSelectedLicenceToRenew] = useState<Licence | undefined>(undefined);
  const [openRenewLicenceModal, setOpenRenewLicenceModal] = useState(false);
  const [openCancelAutoRenewalConfirm, setOpenCancelAutoRenewalConfirm] = useState(false);
  const [selectedLicenceToCancelAutoRenewal, setSelectedLicenceToCancelAutoRenewal] = useState<Licence | undefined>(
    undefined,
  );

  const handleAddLicenceModalClose = () => {
    setOpenAddLicenceModal(false);
    addLicenceStatus.reset();
  };

  const handleRenewLicenceModalClose = () => {
    setOpenRenewLicenceModal(false);
    renewLicenceStatus.reset();
  };

  const handleRenewLicenceModalSubmit = (
    productName: string,
    totalPrice: number,
    numberOfUsers: number,
    licenceType: string,
  ) => {
    selectedLicenceToRenew &&
      onRenewLicence(productName, totalPrice, numberOfUsers, licenceType, selectedLicenceToRenew.id);
  };

  const handleCancelAutoRenewalConfirmed = () => {
    selectedLicenceToCancelAutoRenewal && onCancelAutoRenewal(selectedLicenceToCancelAutoRenewal.id);
  };

  useEffect(() => {
    if (addLicenceStatus.successful) {
      toastSuccess("Licence added successfully!");
      setOpenAddLicenceModal(false);
    }
  }, [addLicenceStatus.successful]);

  useEffect(() => {
    if (renewLicenceStatus.successful) {
      toastSuccess("The licence has been renewed successfully!");
      setOpenRenewLicenceModal(false);
    }
  }, [renewLicenceStatus.successful]);

  useEffect(() => {
    if (cancelAutoRenewalStatus.successful) {
      toastSuccess("The auto renewal for this licence has been cancelled successfully!");
      setOpenCancelAutoRenewalConfirm(false);
    }
  }, [cancelAutoRenewalStatus.successful]);

  useEffect(() => {
    if (archiveLicenceStatus.successful) {
      toastSuccess("Licence archived successfully!");
    }
  }, [archiveLicenceStatus.successful]);

  const handleIsArchivedFilterChange = (e: any, { name, value }: any) => {
    onIsArchivedFilter(value === "" ? null : value);
  };

  const handleRenewLicence = (licenceId: string) => {
    const licence = licences?.find((l) => l.id === licenceId);
    if (licence) {
      setSelectedLicenceToRenew({ ...licence } as Licence);
      setOpenRenewLicenceModal(true);
    }
  };

  const handleCancelAutoRenewal = (licenceId: string) => {
    const licence = licences?.find((l) => l.id === licenceId);
    if (licence) {
      setSelectedLicenceToCancelAutoRenewal({ ...licence } as Licence);
      setOpenCancelAutoRenewalConfirm(true);
    }
  };

  return (
    <>
      <Menu pointing secondary color="green">
        <Menu.Item icon="plus" name="New licence" onClick={(e) => setOpenAddLicenceModal(true)} />

        <Menu.Menu position="right">
          <Menu.Item>
            <Dropdown
              placeholder="View"
              basic
              clearable
              onChange={handleIsArchivedFilterChange}
              value={isArchivedFilterValue === null ? undefined : isArchivedFilterValue}
              options={[
                { value: false, key: "not archived", text: "not archived" },
                { value: true, key: "archived", text: "archived" },
              ]}
            ></Dropdown>
          </Menu.Item>
        </Menu.Menu>
      </Menu>

      <Segment basic loading={isLicencesLoading}>
        <Grid columns="1">
          <Grid.Row>
            <Grid.Column width="16">
              <LicencesGrid
                licences={licences}
                pageCount={pageCount}
                onPageInfoChange={onPageInfoChange}
                onRenewLicence={handleRenewLicence}
                onCancelAutoRenewal={handleCancelAutoRenewal}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>

      <AddLicenceModal
        open={openAddLicenceModal}
        onClose={handleAddLicenceModalClose}
        onSubmit={onAddLicence}
        submitInProgress={addLicenceStatus.loading}
        submitErrors={addLicenceStatus.errors}
        products={products}
      ></AddLicenceModal>

      <ChangeLicenceProductModal
        open={openRenewLicenceModal}
        onClose={handleRenewLicenceModalClose}
        onSubmit={handleRenewLicenceModalSubmit}
        submitInProgress={renewLicenceStatus.loading}
        submitErrors={renewLicenceStatus.errors}
        products={products}
      ></ChangeLicenceProductModal>

      <Confirm
        open={openCancelAutoRenewalConfirm}
        onCancel={() => setOpenCancelAutoRenewalConfirm(false)}
        onConfirm={handleCancelAutoRenewalConfirmed}
      />
    </>
  );
};

export default LicencesList;
